/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

/* Personalized Please: product-card hover videos. Draft theme only. */
(() => {
  const hoverVideos = {
    'custom-20-oz-tumblers-with-company-logo': 'https://cdn.shopify.com/videos/c/vp/484b411f9d534af294970f6e28018b2c/484b411f9d534af294970f6e28018b2c.HD-720p-1.6Mbps-93915606.mp4',
    'custom-whiskey-glasses-with-company-logo': 'https://cdn.shopify.com/videos/c/vp/c351bab2877c485d9e943aeeadc5b1e8/c351bab2877c485d9e943aeeadc5b1e8.HD-720p-1.6Mbps-93920820.mp4',
  };

  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  const style = document.createElement('style');
  style.id = 'pp-product-hover-video-styles';
  style.textContent = `
    .pp-product-hover-video {
      position: absolute;
      inset: 0;
      z-index: 3;
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
      opacity: 0;
      pointer-events: none;
      background: rgb(var(--color-background));
      transition: opacity 110ms ease;
    }
    @media (hover: hover) and (pointer: fine) and (min-width: 990px) {
      .product-card-wrapper.pp-has-hover-video:hover .media.media--hover-effect > img:first-of-type {
        opacity: 1 !important;
      }
      .product-card-wrapper.pp-has-hover-video:hover .media.media--hover-effect > img + img {
        opacity: 0 !important;
      }
      .product-card-wrapper.pp-has-hover-video:hover .pp-product-hover-video {
        opacity: 1;
      }
    }
  `;
  if (!document.getElementById(style.id)) document.head.appendChild(style);

  function enhanceProductCards(root = document) {
    root.querySelectorAll('.product-card-wrapper').forEach((card) => {
      if (card.dataset.ppHoverVideoReady === 'true') return;

      const link = card.querySelector('a[href*="/products/"]');
      if (!link) return;

      let handle = '';
      try {
        const pathname = new URL(link.href, window.location.origin).pathname;
        const match = pathname.match(/\/products\/([^/?#]+)/);
        handle = match ? match[1] : '';
      } catch {
        return;
      }

      const videoUrl = hoverVideos[handle];
      if (!videoUrl) return;

      const media = card.querySelector('.media.media--hover-effect');
      if (!media) return;

      const video = document.createElement('video');
      video.className = 'pp-product-hover-video';
      video.src = videoUrl;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.setAttribute('aria-hidden', 'true');
      video.setAttribute('tabindex', '-1');
      media.appendChild(video);

      card.classList.add('pp-has-hover-video');
      card.dataset.ppHoverVideoReady = 'true';

      card.addEventListener('mouseenter', () => {
        const playPromise = video.play();
        if (playPromise?.catch) playPromise.catch(() => {});
      });

      card.addEventListener('mouseleave', () => {
        video.pause();
        try {
          video.currentTime = 0;
        } catch {}
      });
    });
  }

  function initHoverVideos() {
    enhanceProductCards();
    const observer = new MutationObserver(() => enhanceProductCards());
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHoverVideos, { once: true });
  } else {
    initHoverVideos();
  }
})();
